/**
 * constants.js
 * ------------
 * Single source of truth for:
 *   - Design tokens (colors, CSS)
 *   - Application enums (roles, statuses)
 *   - Seed / mock data (mirrors Supabase table schemas exactly)
 *
 * DATA MODEL NOTE:
 * Each seed array maps 1-to-1 with a Supabase table.
 * Field names match the database column names so that Supabase
 * query results can be dropped in as direct replacements.
 */

// ================================================================
// DESIGN TOKENS
// ================================================================
export const C = {
  ember:    '#E85D04',
  emberL:   '#F48C06',
  coal:     '#0D0D0D',
  charcoal: '#1A1A1A',
  ash:      '#2E2E2E',
  smoke:    '#5C5C5C',
  mist:     '#A0A0A0',
  cream:    '#F5F0E8',
  leaf:     '#4A7C59',
  leafL:    '#6AAF7A',
  danger:   '#DC2626',
  warning:  '#F59E0B',
  success:  '#10B981',
};

export const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@300;400;500;600&family=Space+Mono:wght@400;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',sans-serif;background:#0D0D0D;color:#F5F0E8;overflow-x:hidden}
.pf{font-family:'Playfair Display',serif}
.mono{font-family:'Space Mono',monospace}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:#1A1A1A}::-webkit-scrollbar-thumb{background:#E85D04;border-radius:3px}
@keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
@keyframes flicker{0%,100%{opacity:1}50%{opacity:.8}}
@keyframes emberFloat{0%,100%{transform:translateY(0) rotate(0deg);opacity:.7}50%{transform:translateY(-20px) rotate(180deg);opacity:1}}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
.fu{animation:fadeUp .5s ease forwards}
.fu2{animation:fadeUp .5s .12s ease forwards;opacity:0}
.fu3{animation:fadeUp .5s .24s ease forwards;opacity:0}
.fu4{animation:fadeUp .5s .36s ease forwards;opacity:0}
.card{background:#1A1A1A;border:1px solid #2E2E2E;border-radius:12px;transition:all .25s ease}
.card:hover{border-color:#4A7C59}
.card-ember:hover{border-color:#E85D04!important}
.btn-p{background:#E85D04;color:#fff;border:none;padding:11px 24px;border-radius:8px;font-family:'DM Sans',sans-serif;font-weight:600;font-size:14px;cursor:pointer;transition:all .2s}
.btn-p:hover{background:#F48C06;transform:translateY(-1px);box-shadow:0 6px 20px rgba(232,93,4,.35)}
.btn-p:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none}
.btn-green{background:#4A7C59;color:#fff;border:none;padding:11px 24px;border-radius:8px;font-family:'DM Sans',sans-serif;font-weight:600;font-size:14px;cursor:pointer;transition:all .2s}
.btn-green:hover{background:#6AAF7A;transform:translateY(-1px);box-shadow:0 6px 20px rgba(74,124,89,.35)}
.btn-s{background:transparent;color:#F5F0E8;border:1px solid #2E2E2E;padding:11px 24px;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:14px;cursor:pointer;transition:all .2s}
.btn-s:hover{border-color:#4A7C59;color:#6AAF7A}
.btn-g{background:transparent;color:#A0A0A0;border:none;padding:7px 14px;border-radius:6px;font-family:'DM Sans',sans-serif;font-size:13px;cursor:pointer;transition:all .2s}
.btn-g:hover{background:#2E2E2E;color:#F5F0E8}
.inp{background:#2E2E2E;border:1px solid #3A3A3A;color:#F5F0E8;padding:11px 14px;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:14px;width:100%;outline:none;transition:border-color .2s}
.inp:focus{border-color:#4A7C59}
.inp::placeholder{color:#5C5C5C}
select.inp{appearance:none;cursor:pointer}
select option{background:#2E2E2E;color:#F5F0E8}
.badge{display:inline-flex;align-items:center;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:700;font-family:'Space Mono',monospace;letter-spacing:.3px}
.b-pending{background:rgba(245,158,11,.12);color:#F59E0B;border:1px solid rgba(245,158,11,.25)}
.b-processing{background:rgba(59,130,246,.12);color:#60A5FA;border:1px solid rgba(59,130,246,.25)}
.b-dispatched{background:rgba(139,92,246,.12);color:#A78BFA;border:1px solid rgba(139,92,246,.25)}
.b-delivered{background:rgba(16,185,129,.12);color:#10B981;border:1px solid rgba(16,185,129,.25)}
.b-cancelled{background:rgba(220,38,38,.12);color:#F87171;border:1px solid rgba(220,38,38,.25)}
.b-active{background:rgba(16,185,129,.12);color:#10B981;border:1px solid rgba(16,185,129,.25)}
.b-disabled{background:rgba(92,92,92,.15);color:#5C5C5C;border:1px solid rgba(92,92,92,.3)}
.b-scheduled{background:rgba(74,124,89,.12);color:#6AAF7A;border:1px solid rgba(74,124,89,.25)}
.b-completed{background:rgba(16,185,129,.12);color:#10B981;border:1px solid rgba(16,185,129,.25)}
.b-partner{background:rgba(74,124,89,.12);color:#6AAF7A;border:1px solid rgba(74,124,89,.25)}
.b-prospect{background:rgba(245,158,11,.12);color:#F59E0B;border:1px solid rgba(245,158,11,.25)}
.tr{border-bottom:1px solid #2E2E2E;transition:background .15s}
.tr:hover{background:rgba(255,255,255,.025)}
.sidebar{background:#1A1A1A;border-right:1px solid #2E2E2E}
.modal-ov{position:fixed;inset:0;background:rgba(0,0,0,.82);z-index:999;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(8px)}
.modal-bx{background:#1A1A1A;border:1px solid #2E2E2E;border-radius:16px;padding:28px;width:100%;max-width:520px;max-height:90vh;overflow-y:auto;animation:fadeUp .2s ease}
.tab-btn{background:transparent;border:none;padding:8px 16px;border-radius:7px;font-family:'DM Sans',sans-serif;font-size:13px;cursor:pointer;transition:all .15s;color:#5C5C5C}
.tab-btn.active{background:rgba(74,124,89,.15);color:#6AAF7A;font-weight:600}

/* ================================================================
   MOBILE RESPONSIVE STYLES
   ================================================================ */
@media (max-width: 768px) {
  body { overflow-x: hidden !important; }
  html { overflow-x: hidden !important; }

  /* Navbar */
  nav {
    padding: 0 3% !important;
    overflow: hidden !important;
  }
  nav > div:last-child {
    gap: 0 !important;
    flex-wrap: wrap !important;
    justify-content: flex-end !important;
  }
  nav > div:last-child button {
    padding: 6px 8px !important;
    font-size: 11px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    white-space: nowrap !important;
  }

  /* Typography scaling */
  h1 {
    font-size: clamp(20px, 6vw, 38px) !important;
    max-width: 100% !important;
    word-break: break-word !important;
  }
  h2 {
    font-size: clamp(18px, 5vw, 34px) !important;
    max-width: 100% !important;
    word-break: break-word !important;
  }
  h3 {
    font-size: clamp(16px, 4vw, 21px) !important;
    word-break: break-word !important;
  }

  /* Text overflow prevention */
  p, div, span, a {
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }

  /* Section padding and width */
  section {
    padding: 50px 4% !important;
    max-width: 100vw !important;
    overflow-x: hidden !important;
    box-sizing: border-box !important;
  }

  /* Hero section - prevent navbar overlap */
  section:first-of-type {
    padding-top: 120px !important;
  }

  /* Remove fixed maxWidths on paragraphs */
  p[style*="maxWidth"] { max-width: 100% !important; }

  /* Cards */
  .card {
    margin: 0 auto !important;
    width: 100% !important;
    box-sizing: border-box !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }

  /* Buttons - ensure touch size and prevent overflow */
  button {
    min-height: 44px !important;
    padding: 12px 16px !important;
    font-size: 14px !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  /* Input fields - bigger touch targets */
  .inp {
    min-height: 44px !important;
    padding: 12px 16px !important;
    font-size: 16px !important;
    width: 100% !important;
    box-sizing: border-box !important;
    max-width: 100% !important;
  }

  select.inp {
    min-height: 44px !important;
    width: 100% !important;
  }

  /* Modal adjustments */
  .modal-ov {
    padding: 16px !important;
    overflow-x: hidden !important;
  }
  .modal-bx {
    padding: 20px !important;
    border-radius: 12px !important;
    max-width: 95vw !important;
    width: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }
}

@media (max-width: 480px) {
  /* Extra small adjustments */
  section {
    padding: 40px 3% !important;
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }

  /* Hero section - extra padding to clear navbar */
  section:first-of-type {
    padding-top: 140px !important;
  }

  h1 {
    font-size: clamp(18px, 5vw, 28px) !important;
    line-height: 1.1 !important;
  }
  h2 {
    font-size: clamp(16px, 4vw, 24px) !important;
    line-height: 1.1 !important;
  }
  h3 {
    font-size: clamp(14px, 3.5vw, 18px) !important;
  }

  .pf {
    line-height: 1.1 !important;
    word-break: break-word !important;
  }

  /* Cards full-width */
  .card {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  /* Prevent horizontal scrolling */
  body, html {
    overflow-x: hidden !important;
    width: 100vw !important;
  }

  /* Footer text wrapping */
  footer {
    padding: 20px 3% !important;
    overflow-x: hidden !important;
  }
  footer p {
    font-size: 11px !important;
    word-break: break-word !important;
    max-width: 100% !important;
  }
  footer span {
    font-size: 11px !important;
    display: block !important;
    margin: 2px 0 !important;
  }

  /* Spacing tightening */
  [style*="gap: 28"] { gap: 12px !important; }
  [style*="gap: 60"] { gap: 16px !important; }
  [style*="marginTop: 44"] { margin-top: 20px !important; }
  [style*="marginBottom: 44"] { margin-bottom: 20px !important; }
}
`;

// ================================================================
// APPLICATION ENUMS
// ================================================================

/** Maps roles to the dashboard sections they can access. */
export const ROLE_ACCESS = {
  'Admin':              ['overview','orders','deliveries','inventory','waste','finance','employees'],
  'Operations Manager': ['overview','orders','deliveries','inventory','waste'],
  'Finance Manager':    ['overview','finance'],
  'Delivery Staff':     ['deliveries'],
};

export const ORDER_STATUSES   = ['pending','processing','dispatched','delivered','cancelled'];
export const STAFF_ROLES      = ['Admin','Operations Manager','Finance Manager','Delivery Staff'];
export const WAREHOUSES        = ['Nakuru Main','Nakuru Annex','Nairobi Depot'];
export const PICKUP_STATUSES  = ['scheduled','completed','cancelled'];

export const DISCOUNT_TIERS = [
  { tier:'Bronze',   minKg:50,  maxKg:149,  rate:5,  color:'#CD7F32' },
  { tier:'Silver',   minKg:150, maxKg:299,  rate:8,  color:'#A0A0A0' },
  { tier:'Gold',     minKg:300, maxKg:499,  rate:12, color:'#F59E0B' },
  { tier:'Platinum', minKg:500, maxKg:null, rate:15, color:'#6AAF7A' },
];

/** Returns the discount tier for a given monthly waste volume. */
export function getDiscountTier(kg) {
  return DISCOUNT_TIERS.find(t => kg >= t.minKg && (t.maxKg === null || kg <= t.maxKg)) || null;
}

export const BRIQUETTE_YIELD_RATE = 0.65;  // kg of briquettes per kg of waste
export const CO2_OFFSET_RATE      = 0.38;  // kg CO2 offset per kg of waste diverted

// ================================================================
// PRODUCTS (static catalogue — no backend table needed yet)
// ================================================================
export const PRODUCTS = [
  {
    name:'Household Briquettes',
    price:'KSh 40 per kg',
    sizes:'5kg, 10kg, 25kg',
    icon:'\u{1F525}',
    available:true,
    bestFor:['Homes', 'Small kitchens', 'Occasional use'],
    desc:'Clean-burning briquettes packed for everyday home cooking, heating, and BBQ use.',
    points:['Easy to light and store', 'Cleaner cooking', 'Affordable for daily household use'],
  },
  {
    name:'Commercial Briquettes',
    price:'KSh 35 per kg',
    sizes:'50kg, 100kg bags',
    icon:'\u{1F3ED}',
    available:true,
    bestFor:['Hotels', 'Restaurants', 'Catering businesses'],
    desc:'Reliable briquette supply for busy kitchens that need steady heat and lower fuel costs.',
    points:['Consistent heat for long cooking hours', 'Suitable for high-demand kitchens', 'Helps reduce charcoal expenses'],
  },
  {
    name:'Bulk Supply',
    price:'Contact for pricing',
    sizes:'500kg, 1000kg+',
    icon:'\u{1F4E6}',
    available:true,
    bestFor:['Distributors', 'Institutions', 'Large-scale buyers'],
    desc:'High-volume briquette supply for buyers who need consistent stock and long-term supply.',
    points:['Discounted pricing for bulk orders', 'Suitable for resale and institutions', 'Long-term supply arrangements available'],
  },
];

// ================================================================
// FORMSPREE ENDPOINTS (public website forms)
// ================================================================
// Replace the placeholder IDs with your real Formspree form IDs.
export const FORMSPREE_ORDER_ENDPOINT   = 'https://formspree.io/f/xvzdgvdl';
export const FORMSPREE_WASTE_ENDPOINT   = 'https://formspree.io/f/xaqanlvn';
export const FORMSPREE_CONTACT_ENDPOINT = 'https://formspree.io/f/mlgadozj';

// ================================================================
// MOCK STAFF USERS
// These mirror the `profiles` table joined with auth.users.
// In production: replaced by supabase.auth.signInWithPassword()
// and a profiles table query.
// ================================================================

/**
 * @typedef {Object} StaffUser
 * @property {string} id
 * @property {string} email
 * @property {string} password  -- Never stored in DB; auth handled by Supabase Auth
 * @property {string} name
 * @property {string} role      -- Matches STAFF_ROLES enum
 * @property {string} dept
 * @property {string} initials
 */
export const MOCK_USERS = [
  { id:'usr-1', email:'admin@maabriquette.co.ke',   password:'Admin@123',   name:'David Waweru',  role:'Admin',              dept:'Management', initials:'DW' },
  { id:'usr-2', email:'grace@maabriquette.co.ke',   password:'Ops@123',     name:'Grace Wambui',  role:'Operations Manager', dept:'Operations', initials:'GW' },
  { id:'usr-3', email:'amina@maabriquette.co.ke',   password:'Finance@123', name:'Amina Hassan',  role:'Finance Manager',    dept:'Finance',    initials:'AH' },
  { id:'usr-4', email:'peter@maabriquette.co.ke',   password:'Deliver@123', name:'Peter Ochieng', role:'Delivery Staff',     dept:'Logistics',  initials:'PO' },
  { id:'usr-5', email:'samuel@maabriquette.co.ke',  password:'Deliver@123', name:'Samuel Mwangi', role:'Delivery Staff',     dept:'Logistics',  initials:'SM' },
];

// ================================================================
// SEED DATA — mirrors Supabase table schemas exactly
// When Supabase is connected, these are replaced by live queries.
// Field names must not be renamed without updating the API modules.
// ================================================================

/**
 * Supabase table: orders
 * @typedef {Object} Order
 * @property {string}  id          -- e.g. "ORD-001"
 * @property {string}  customer
 * @property {string}  product
 * @property {string}  qty         -- e.g. "500 kg"
 * @property {string}  location
 * @property {string}  status      -- ORDER_STATUSES enum
 * @property {string}  date        -- display date, e.g. "Mar 14"
 * @property {string}  created_at  -- ISO timestamp (set by Supabase)
 */
export const SEED_ORDERS = [
  { id:'ORD-001', customer:'Nakuru Hotels Ltd',  product:'BBQ Briquettes',        qty:'500 kg',   location:'Nakuru Town',  status:'delivered',  date:'Mar 14', created_at:'2026-03-14T09:00:00Z' },
  { id:'ORD-002', customer:'John Kamau',          product:'Bulk Package',          qty:'200 kg',   location:'Nairobi CBD',  status:'dispatched', date:'Mar 15', created_at:'2026-03-15T10:00:00Z' },
  { id:'ORD-003', customer:'Mombasa Grill Co.',   product:'Industrial Briquettes', qty:'1,000 kg', location:'Mombasa Port', status:'processing', date:'Mar 15', created_at:'2026-03-15T11:30:00Z' },
  { id:'ORD-004', customer:'Grace Wanjiku',       product:'BBQ Briquettes',        qty:'50 kg',    location:'Thika Road',   status:'pending',    date:'Mar 16', created_at:'2026-03-16T08:00:00Z' },
  { id:'ORD-005', customer:'Safari Park Hotel',   product:'BBQ Briquettes',        qty:'300 kg',   location:'Nairobi',      status:'delivered',  date:'Mar 13', created_at:'2026-03-13T14:00:00Z' },
  { id:'ORD-006', customer:'Eldoret Bakery',      product:'Industrial Briquettes', qty:'750 kg',   location:'Eldoret',      status:'cancelled',  date:'Mar 12', created_at:'2026-03-12T13:00:00Z' },
];

/**
 * Supabase table: deliveries
 * @typedef {Object} Delivery
 * @property {string} id
 * @property {string} driver
 * @property {string} vehicle
 * @property {string} destination
 * @property {string} status       -- ORDER_STATUSES enum
 * @property {string} date         -- ISO date
 * @property {string} fuel         -- display string e.g. "KSh 3,200"
 * @property {string} notes
 * @property {string} created_at
 */
export const SEED_DELIVERIES = [
  { id:'DEL-001', driver:'Peter Ochieng',  vehicle:'KBZ 234G', destination:'Nakuru Town',  status:'delivered',  date:'2026-03-14', fuel:'KSh 3,200', notes:'Delivered on time',   created_at:'2026-03-14T08:00:00Z' },
  { id:'DEL-002', driver:'Samuel Mwangi', vehicle:'KDA 891F', destination:'Nairobi CBD',  status:'dispatched', date:'2026-03-15', fuel:'KSh 1,800', notes:'En route',             created_at:'2026-03-15T07:30:00Z' },
  { id:'DEL-003', driver:'Peter Ochieng',  vehicle:'KBZ 234G', destination:'Mombasa Port', status:'processing', date:'2026-03-16', fuel:'KSh 8,500', notes:'Long haul prep',       created_at:'2026-03-15T16:00:00Z' },
];

/**
 * Supabase table: inventory
 * @typedef {Object} InventoryItem
 * @property {number}  id
 * @property {string}  product
 * @property {number}  stock       -- kg on hand
 * @property {string}  batch
 * @property {string}  warehouse   -- WAREHOUSES enum
 * @property {boolean} alert       -- true when stock < LOW_STOCK_THRESHOLD
 * @property {string}  updated_at
 */
export const LOW_STOCK_THRESHOLD = 100;

export const SEED_INVENTORY = [
  { id:1, product:'BBQ Briquettes (10kg)',        stock:2840, batch:'B-2024-031', warehouse:'Nakuru Main',   alert:false, updated_at:'2026-03-15T00:00:00Z' },
  { id:2, product:'Industrial Briquettes (50kg)', stock:420,  batch:'B-2024-030', warehouse:'Nakuru Main',   alert:false, updated_at:'2026-03-15T00:00:00Z' },
  { id:3, product:'Bulk Supply (500kg)',           stock:38,   batch:'B-2024-029', warehouse:'Nakuru Annex',  alert:true,  updated_at:'2026-03-14T00:00:00Z' },
  { id:4, product:'BBQ Starter Pack (5kg)',        stock:94,   batch:'B-2024-031', warehouse:'Nairobi Depot', alert:true,  updated_at:'2026-03-14T00:00:00Z' },
];

/**
 * Supabase table: expenses
 * @typedef {Object} Expense
 * @property {number} id
 * @property {string} category
 * @property {number} amount      -- KSh
 * @property {string} icon
 * @property {string} month       -- e.g. "Mar"
 * @property {string} created_at
 */
export const SEED_EXPENSES = [
  { id:1, category:'Raw Materials', amount:145000, icon:'🌿', month:'Mar', created_at:'2026-03-01T00:00:00Z' },
  { id:2, category:'Transport',     amount:42000,  icon:'🚚', month:'Mar', created_at:'2026-03-01T00:00:00Z' },
  { id:3, category:'Salaries',      amount:210000, icon:'👥', month:'Mar', created_at:'2026-03-01T00:00:00Z' },
  { id:4, category:'Fuel',          amount:28000,  icon:'⛽', month:'Mar', created_at:'2026-03-01T00:00:00Z' },
  { id:5, category:'Equipment',     amount:15000,  icon:'🔧', month:'Mar', created_at:'2026-03-01T00:00:00Z' },
];

/**
 * Supabase table: profiles  (extends auth.users)
 * @typedef {Object} Employee
 * @property {string} id       -- matches auth.users.id (uuid)
 * @property {string} name
 * @property {string} role     -- STAFF_ROLES enum
 * @property {string} dept
 * @property {string} email
 * @property {string} status   -- "active" | "disabled"
 * @property {string} initials
 * @property {string} created_at
 */
export const SEED_EMPLOYEES = [
  { id:'usr-1', name:'David Waweru',  role:'Admin',              dept:'Management', email:'david.w@maabriquette.co.ke',  status:'active', initials:'DW', created_at:'2025-01-01T00:00:00Z' },
  { id:'usr-2', name:'Amina Hassan',  role:'Finance Manager',    dept:'Finance',    email:'amina.h@maabriquette.co.ke',  status:'active', initials:'AH', created_at:'2025-01-01T00:00:00Z' },
  { id:'usr-3', name:'Peter Ochieng', role:'Delivery Staff',     dept:'Logistics',  email:'peter.o@maabriquette.co.ke',  status:'active', initials:'PO', created_at:'2025-03-01T00:00:00Z' },
  { id:'usr-4', name:'Grace Wambui',  role:'Operations Manager', dept:'Operations', email:'grace.w@maabriquette.co.ke',  status:'active', initials:'GW', created_at:'2025-01-01T00:00:00Z' },
  { id:'usr-5', name:'Samuel Mwangi', role:'Delivery Staff',     dept:'Logistics',  email:'samuel.m@maabriquette.co.ke', status:'active', initials:'SM', created_at:'2025-04-01T00:00:00Z' },
];

/**
 * Supabase table: partner_hotels
 * @typedef {Object} Hotel
 * @property {string} id
 * @property {string} name
 * @property {string} location
 * @property {string} contact
 * @property {string} phone
 * @property {string} email
 * @property {number} waste_volume_kg  -- estimated monthly volume
 * @property {string} status           -- "partner" | "prospect"
 * @property {string} joined           -- ISO date
 * @property {number} discount_rate    -- percentage
 * @property {string} created_at
 */
export const SEED_HOTELS = [
  { id:'HTL-001', name:'Sarova Whitesands',      location:'Mombasa',  contact:'manager@sarovawhite.co.ke',   phone:'+254 711 001 001', waste_volume_kg:420, status:'partner',  joined:'2025-08-10', discount_rate:12, created_at:'2025-08-10T00:00:00Z' },
  { id:'HTL-002', name:'Nakuru Flamingo Hotel',  location:'Nakuru',   contact:'ops@flamingonakuru.co.ke',    phone:'+254 722 002 002', waste_volume_kg:280, status:'partner',  joined:'2025-10-01', discount_rate:8,  created_at:'2025-10-01T00:00:00Z' },
  { id:'HTL-003', name:'Ole Sereni Nairobi',     location:'Nairobi',  contact:'procurement@olesereni.co.ke', phone:'+254 733 003 003', waste_volume_kg:610, status:'partner',  joined:'2025-06-15', discount_rate:15, created_at:'2025-06-15T00:00:00Z' },
  { id:'HTL-004', name:'Eldoret Wagon Hotel',    location:'Eldoret',  contact:'info@wagonhotel.co.ke',       phone:'+254 744 004 004', waste_volume_kg:190, status:'prospect', joined:'2026-02-20', discount_rate:5,  created_at:'2026-02-20T00:00:00Z' },
  { id:'HTL-005', name:'Kisumu Imperial Hotel',  location:'Kisumu',   contact:'gm@kisumulimperial.co.ke',    phone:'+254 755 005 005', waste_volume_kg:320, status:'partner',  joined:'2025-12-05', discount_rate:9,  created_at:'2025-12-05T00:00:00Z' },
];

/**
 * Supabase table: waste_collection_logs
 * @typedef {Object} WasteLog
 * @property {string} id
 * @property {string} hotel_id        -- FK partner_hotels.id
 * @property {string} hotel_name      -- denormalised for display
 * @property {string} date            -- ISO date of collection
 * @property {number} waste_kg
 * @property {string} collected_by    -- staff name
 * @property {string} notes
 * @property {string} created_at
 */
export const SEED_LOGS = [
  { id:'WCL-001', hotel_id:'HTL-001', hotel_name:'Sarova Whitesands',     date:'2026-03-01', waste_kg:105, collected_by:'Peter Ochieng',  notes:'Kitchen & restaurant waste', created_at:'2026-03-01T10:00:00Z' },
  { id:'WCL-002', hotel_id:'HTL-002', hotel_name:'Nakuru Flamingo Hotel', date:'2026-03-03', waste_kg:68,  collected_by:'Samuel Mwangi',  notes:'Organic food waste',          created_at:'2026-03-03T09:00:00Z' },
  { id:'WCL-003', hotel_id:'HTL-003', hotel_name:'Ole Sereni Nairobi',    date:'2026-03-05', waste_kg:148, collected_by:'Peter Ochieng',  notes:'Bulk kitchen waste — 2 trips', created_at:'2026-03-05T08:30:00Z' },
  { id:'WCL-004', hotel_id:'HTL-001', hotel_name:'Sarova Whitesands',     date:'2026-03-08', waste_kg:112, collected_by:'Peter Ochieng',  notes:'Weekly collection',           created_at:'2026-03-08T10:00:00Z' },
  { id:'WCL-005', hotel_id:'HTL-005', hotel_name:'Kisumu Imperial Hotel', date:'2026-03-10', waste_kg:80,  collected_by:'Samuel Mwangi',  notes:'First collection this month', created_at:'2026-03-10T11:00:00Z' },
  { id:'WCL-006', hotel_id:'HTL-002', hotel_name:'Nakuru Flamingo Hotel', date:'2026-03-12', waste_kg:72,  collected_by:'Peter Ochieng',  notes:'Standard weekly pickup',      created_at:'2026-03-12T09:30:00Z' },
  { id:'WCL-007', hotel_id:'HTL-003', hotel_name:'Ole Sereni Nairobi',    date:'2026-03-15', waste_kg:155, collected_by:'Samuel Mwangi',  notes:'Increased volume — event week', created_at:'2026-03-15T08:00:00Z' },
];

/**
 * Supabase table: pickup_schedules
 * @typedef {Object} PickupSchedule
 * @property {string} id
 * @property {string} hotel_id        -- FK partner_hotels.id
 * @property {string} hotel_name
 * @property {string} scheduled_date  -- ISO date
 * @property {string} status          -- PICKUP_STATUSES enum
 * @property {string} driver
 * @property {string} vehicle
 * @property {string} notes
 * @property {string} created_at
 */
export const SEED_SCHEDULES = [
  { id:'PKP-001', hotel_id:'HTL-001', hotel_name:'Sarova Whitesands',     scheduled_date:'2026-03-22', status:'scheduled', driver:'Peter Ochieng',  vehicle:'KBZ 234G', notes:'Bi-weekly pickup',                 created_at:'2026-03-18T00:00:00Z' },
  { id:'PKP-002', hotel_id:'HTL-002', hotel_name:'Nakuru Flamingo Hotel', scheduled_date:'2026-03-23', status:'scheduled', driver:'Samuel Mwangi',  vehicle:'KDA 891F', notes:'Standard weekly',                  created_at:'2026-03-18T00:00:00Z' },
  { id:'PKP-003', hotel_id:'HTL-003', hotel_name:'Ole Sereni Nairobi',    scheduled_date:'2026-03-24', status:'scheduled', driver:'Peter Ochieng',  vehicle:'KBZ 234G', notes:'Large volume — confirm capacity',  created_at:'2026-03-18T00:00:00Z' },
  { id:'PKP-004', hotel_id:'HTL-005', hotel_name:'Kisumu Imperial Hotel', scheduled_date:'2026-03-19', status:'completed', driver:'Samuel Mwangi',  vehicle:'KDA 891F', notes:'Completed on time',               created_at:'2026-03-10T00:00:00Z' },
  { id:'PKP-005', hotel_id:'HTL-004', hotel_name:'Eldoret Wagon Hotel',   scheduled_date:'2026-03-28', status:'scheduled', driver:'Peter Ochieng',  vehicle:'KBZ 234G', notes:'First pickup — prospect onboarding', created_at:'2026-03-20T00:00:00Z' },
];

/**
 * Supabase table: discount_tracking
 * @typedef {Object} DiscountRecord
 * @property {string}      id
 * @property {string}      hotel_id      -- FK partner_hotels.id
 * @property {string}      hotel_name
 * @property {string}      month         -- e.g. "Feb 2026"
 * @property {number}      waste_kg      -- total waste that month
 * @property {number}      discount_pct
 * @property {number}      discount_amt  -- KSh value of discount
 * @property {string|null} order_id      -- FK orders.id (null until applied)
 * @property {boolean}     applied
 * @property {string}      created_at
 */
export const SEED_DISCOUNTS = [
  { id:'DSC-001', hotel_id:'HTL-001', hotel_name:'Sarova Whitesands',     month:'Feb 2026', waste_kg:410, discount_pct:12, discount_amt:14400, order_id:'ORD-005', applied:true,  created_at:'2026-03-01T00:00:00Z' },
  { id:'DSC-002', hotel_id:'HTL-002', hotel_name:'Nakuru Flamingo Hotel', month:'Feb 2026', waste_kg:270, discount_pct:8,  discount_amt:6200,  order_id:'ORD-001', applied:true,  created_at:'2026-03-01T00:00:00Z' },
  { id:'DSC-003', hotel_id:'HTL-003', hotel_name:'Ole Sereni Nairobi',    month:'Feb 2026', waste_kg:590, discount_pct:15, discount_amt:28800, order_id:'ORD-003', applied:true,  created_at:'2026-03-01T00:00:00Z' },
  { id:'DSC-004', hotel_id:'HTL-005', hotel_name:'Kisumu Imperial Hotel', month:'Feb 2026', waste_kg:300, discount_pct:9,  discount_amt:9000,  order_id:'ORD-002', applied:true,  created_at:'2026-03-01T00:00:00Z' },
  { id:'DSC-005', hotel_id:'HTL-001', hotel_name:'Sarova Whitesands',     month:'Mar 2026', waste_kg:217, discount_pct:12, discount_amt:8400,  order_id:null,      applied:false, created_at:'2026-03-16T00:00:00Z' },
  { id:'DSC-006', hotel_id:'HTL-003', hotel_name:'Ole Sereni Nairobi',    month:'Mar 2026', waste_kg:303, discount_pct:15, discount_amt:14400, order_id:null,      applied:false, created_at:'2026-03-16T00:00:00Z' },
];
